import React from "react";
import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { Logo } from "./Logo";
import { Footer } from "../sections/Footer";

/**
 * LegalPageLayout — shared shell for Privacy, Terms, Contact, About,
 * Security, and Cookie pages. Designed for a clean, corporate, editorial
 * feel: quiet header, generous whitespace, sticky TOC on desktop, and
 * fully responsive mobile stacking.
 */
export const LegalPageLayout = ({
  eyebrow,
  title,
  updated,
  intro,
  sections = [],
  children,
}) => {
  return (
    <div
      className="min-h-screen bg-black text-white"
      data-testid={`legal-page-${(title || "").toLowerCase().replace(/\s+/g, "-")}`}
    >
      {/* Slim top bar */}
      <header className="sticky top-0 z-30 bg-black/85 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-14 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors text-sm font-medium"
            data-testid="legal-back-home"
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={2.5} />
            Back
          </Link>
          <Link to="/" aria-label="EVIQ home" className="flex items-center">
            <Logo size={22} />
          </Link>
          <div className="w-16 flex justify-end">
            <Link
              to="/contact"
              className="hidden sm:inline text-xs font-mono uppercase tracking-[0.18em] text-zinc-500 hover:text-white transition-colors"
            >
              Contact
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-white/5 bg-gradient-to-b from-white/[0.02] to-transparent">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-10 sm:pb-16">
          {eyebrow && (
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-4">
              {eyebrow}
            </div>
          )}
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight">
            {title}
          </h1>
          {updated && (
            <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-zinc-400">
              <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14]" />
              Last updated · {updated}
            </div>
          )}
          {intro && (
            <p className="mt-8 text-base sm:text-lg text-zinc-300 leading-relaxed max-w-3xl">
              {intro}
            </p>
          )}
        </div>
      </section>

      {/* Body: sidebar TOC (desktop) + content */}
      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-12 sm:py-16 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-10 lg:gap-16">
        {sections.length > 0 && (
          <aside className="lg:sticky lg:top-24 lg:self-start" aria-label="Table of contents">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-4">
              On this page
            </div>
            <nav>
              <ol className="space-y-2.5">
                {sections.map((s, idx) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      data-testid={`legal-toc-${s.id}`}
                      className="group flex items-baseline gap-3 text-sm text-zinc-400 hover:text-white transition-colors"
                    >
                      <span className="font-mono text-[10px] text-zinc-600 group-hover:text-[#39FF14] transition-colors shrink-0">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <span className="leading-snug">{s.label}</span>
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          </aside>
        )}

        <article className="prose-legal">{children}</article>
      </main>

      <Footer />
    </div>
  );
};

/** Renders one section with an anchor + heading. */
export const LegalSection = ({ id, title, children }) => (
  <section id={id} className="mb-14 scroll-mt-24" data-testid={`legal-section-${id}`}>
    <h2 className="font-display text-2xl sm:text-3xl font-semibold text-white mb-5 tracking-tight">
      {title}
    </h2>
    <div className="space-y-4 text-[15px] leading-[1.75] text-zinc-300 [&_a]:text-[#00E5FF] [&_a]:underline-offset-2 [&_a:hover]:underline [&_strong]:text-white [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-2 [&_code]:text-[13px] [&_code]:font-mono [&_code]:bg-white/[0.05] [&_code]:border [&_code]:border-white/10 [&_code]:rounded [&_code]:px-1.5 [&_code]:py-0.5">
      {children}
    </div>
  </section>
);
